// lib/qrBridge.js — Passerelle contextuelle desktop → mobile (capture caméra).
//
// Modèle de sécurité : le téléphone doit être connecté au MÊME compte. Le QR ne
// transporte qu'un token opaque servant de clé de lookup ; toute la sécurité est
// portée par la RLS de `mobile_capture_sessions` (user_id = auth.uid()). Le token
// n'est donc JAMAIS une permission — il ne peut pas élargir les droits.
//
// On ne stocke que le HASH du token ; le token en clair ne vit que dans l'URL du QR.
import { supabase } from "@/lib/supabaseClient";

// SHA-256 hex via Web Crypto.
export async function hashToken(token) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function randomToken() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let s = btoa(String.fromCharCode(...bytes));
  return s.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""); // base64url
}

// Desktop : crée une session et renvoie l'URL à encoder dans le QR.
export async function createCaptureSession({ artworkId = null, collectionId = null, actionType, documentType = null }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("not_authenticated");

  const token = randomToken();
  const token_hash = await hashToken(token);

  const { data, error } = await supabase
    .from("mobile_capture_sessions")
    .insert({
      token_hash,
      user_id: user.id,
      artwork_id: artworkId,
      collection_id: collectionId,
      action_type: actionType,
      document_type: documentType,
      status: "pending",
    })
    .select("id, expires_at")
    .single();
  if (error) throw error;

  return { sessionId: data.id, token, url: `${window.location.origin}/m/${token}`, expiresAt: data.expires_at };
}

// Mobile : résout le contexte depuis le token (RLS → même compte uniquement).
// Renvoie { ok, session } ou { ok:false, reason }.
export async function resolveCaptureSession(token) {
  const token_hash = await hashToken(token);
  const { data, error } = await supabase
    .from("mobile_capture_sessions")
    .select("id, artwork_id, collection_id, action_type, document_type, status, expires_at")
    .eq("token_hash", token_hash)
    .maybeSingle();
  if (error) throw error;
  if (!data) return { ok: false, reason: "not_found" };
  if (new Date(data.expires_at) < new Date()) return { ok: false, reason: "expired", session: data };
  if (["completed", "cancelled", "expired"].includes(data.status)) return { ok: false, reason: data.status, session: data };
  return { ok: true, session: data };
}

// Mobile : marque la session « ouverte » (le desktop l'apprend en realtime).
export async function markOpened(id) {
  await supabase
    .from("mobile_capture_sessions")
    .update({ status: "opened", opened_at: new Date().toISOString() })
    .eq("id", id)
    .eq("status", "pending");
}

// Mobile : clôt la session après upload réussi.
export async function completeSession(id, resultPath) {
  const { error } = await supabase
    .from("mobile_capture_sessions")
    .update({ status: "completed", completed_at: new Date().toISOString(), result_path: resultPath })
    .eq("id", id);
  if (error) throw error;
}

// Desktop : abonnement realtime aux changements de statut d'une session.
export function subscribeToSession(sessionId, onChange) {
  const channel = supabase
    .channel(`mcs_${sessionId}`)
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "mobile_capture_sessions", filter: `id=eq.${sessionId}` },
      (payload) => onChange(payload.new)
    )
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}
